"use client";

import { Image as ImageType } from "@/types/Image";
import Image from "next/image";
import Link from "next/link";
import { useEffect, useState } from "react";
import "./index.scss";

const ImageRotation = ({
  images,
  imageHrefs,
}: {
  images: ImageType[];
  imageHrefs: string[];
}) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [autoPlay, setAutoPlay] = useState(true);

  const goToNextImage = () => {
    setCurrentIndex((prevIndex) => (prevIndex + 1) % images.length);
  };

  const goToImage = (imageIndex: number) => {
    setAutoPlay(false);
    setCurrentIndex(imageIndex);
  };

  useEffect(() => {
    if (autoPlay) {
      const interval = setTimeout(() => {
        goToNextImage();
      }, 5000);

      return () => clearTimeout(interval);
    }
  }, [images.length, currentIndex, autoPlay]);

  if (images.length === 0) {
    return null;
  }

  const rotationImageSlides = images.map((image, imageIndex) => {
    const imageElement = (
      <Image
        src={image.src}
        fill={true}
        sizes="100vw"
        alt={image.alt}
        priority={imageIndex === 0}
        style={{ opacity: 1 }}
      />
    );

    return (
      <li
        key={`${image.src}-${imageIndex}`}
        className={`image-container ${
          currentIndex === imageIndex ? "active" : ""
        }`}
        aria-hidden={imageIndex !== currentIndex}
      >
        {imageHrefs[imageIndex] ? (
          <Link
            href={imageHrefs[imageIndex]}
            passHref
            target="_blank"
            rel="noopener noreferrer"
          >
            {imageElement}
          </Link>
        ) : (
          imageElement
        )}
      </li>
    );
  });

  const navigationButtonNodes = images.map((image, imageIndex) => {
    return (
      <li key={`${image.src}-${imageIndex}`}>
        <button
          className={`dot ${imageIndex === currentIndex ? "selected" : ""}`}
          onClick={() => goToImage(imageIndex)}
        >
          View Image #{imageIndex + 1}
        </button>
      </li>
    );
  });

  return (
    <div className="component image-rotation">
      <ul
        style={{
          paddingTop: `${(images[0].height / images[0].width) * 100}%`,
        }}
      >
        {rotationImageSlides}
      </ul>
      <ul className="dot-navigation">{navigationButtonNodes}</ul>

      {autoPlay ? (
        <button
          className={`auto-play pause`}
          onClick={() => setAutoPlay(false)}
        >
          Pause Animation
        </button>
      ) : (
        <button className={`auto-play start`} onClick={() => setAutoPlay(true)}>
          Start Animation
        </button>
      )}
    </div>
  );
};

export default ImageRotation;
